import { List, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { fetchMoreHrTasks } from "../slice/dashboardActions";
import HrTaskListItem from "./HrTaskListItem";
import LoadMoreButton from "./LoadMoreButton";
import { HrTask, HrTasklistQuery } from "./types";

type Props = {
  isLoading: boolean;
  tasks: HrTask[];
  handleSelectHrTask: (taskId: string) => void;
  hrTaskListQuery: HrTasklistQuery;
};

const HrTasklist = ({
  isLoading,
  tasks,
  handleSelectHrTask,
  hrTaskListQuery,
}: Props) => {
  const { t } = useTranslation("dashboard");

  const { cursor } = useAppSelector((state) => state.dashboard.hrTasklist);

  const dispatch = useAppDispatch();

  const handleFetchNext = useCallback(() => {
    if (!cursor) return;
    dispatch(fetchMoreHrTasks({ ...hrTaskListQuery, cursor }));
  }, [cursor, dispatch, hrTaskListQuery]);

  useEffect(() => {
    // if next page is available and task list is empty, fetch next page
    if (cursor && !tasks.length) {
      handleFetchNext();
    }
  }, [cursor, handleFetchNext, tasks.length]);

  const showEmptyState = !isLoading && !tasks.length;
  const emptyStateListStyles = showEmptyState
    ? {
        bgcolor: "#fff",
        borderRadius: "8px",
      }
    : {};

  return (
    <List
      disablePadding
      sx={{
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        ...emptyStateListStyles,
      }}
    >
      {showEmptyState ? (
        <Typography p="16px" color="textSecondary">
          {t("hrTasklist.emptyState")}
        </Typography>
      ) : (
        tasks.map((task, index) => {
          const isLast = index === tasks.length - 1;
          return (
            <HrTaskListItem
              key={task.id}
              task={task}
              isLast={isLast}
              handleSelectHrTask={handleSelectHrTask}
            />
          );
        })
      )}
      <LoadMoreButton onClick={handleFetchNext} cursor={cursor} />
    </List>
  );
};

export default HrTasklist;
